// Widget.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { db, serverTimestamp, auth } from '../lib/firebase';
import { collection, addDoc, getDocs, orderBy, query as firestoreQuery, where } from "firebase/firestore";
import { Link } from "react-router-dom";
import * as XLSX from 'xlsx';
import Header from './Header';


function normalizeText(s = "") {
  return String(s)
    .toLowerCase()
    .replace(/[^\w\s:-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function tokenize(s = "") {
  return normalizeText(s).split(" ").filter(Boolean);
}

function levenshtein(a = "", b = "") {
  const A = String(a);
  const B = String(b);
  const n = A.length;
  const m = B.length;
  if (n === 0) return m;
  if (m === 0) return n;
  const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = 0; i <= n; i++) dp[i][0] = i;
  for (let j = 0; j <= m; j++) dp[0][j] = j;
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      const cost = A[i - 1] === B[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + cost
      );
    }
  }
  return dp[n][m];
}

function scoreCandidateForTokens(candidateRaw, tokens) {
  const candidate = normalizeText(candidateRaw);
  const cWords = candidate.split(" ").filter(Boolean);
  let score = 0;
  let matched = 0;

  for (const token of tokens) {
    let best = 0;

    if (candidate === token) {
      best = Math.max(best, 200);
    }

    if (cWords.some((w) => w === token)) {
      best = Math.max(best, 150);
    }

    if (cWords.some((w) => w.startsWith(token))) {
      best = Math.max(best, 110);
    }

    if (candidate.includes(token)) {
      best = Math.max(best, 70);
    }

    if (token.length >= 3) {
      let minDist = Infinity;
      for (const w of cWords) {
        const d = levenshtein(token, w);
        if (d < minDist) minDist = d;
      }
      const threshold = Math.max(1, Math.floor(token.length * 0.34));
      if (minDist <= threshold) {
        best = Math.max(best, Math.max(20, 60 - minDist * 10));
      }
    }

    if (best > 0) {
      matched += 1;
      score += best;
    } else {
      return { score: 0, matchedCount: matched };
    }
  }

  return { score, matchedCount: matched };
}

function Widget() {
  
  useEffect(() => {
    document.title = "TOC > Income & Expense Tracker";
  }, []);

  const [date, setDate] = useState("");
  const [amount, setAmount] = useState("");
  const [query, setQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const portalRef = useRef(null);
  const inputRef = useRef(null);
  const itemRefs = useRef([]);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const [shake, setShake] = useState(false);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [showThankYou, setShowThankYou] = useState(false);
  const [showThanksText, setShowThanksText] = useState(false);
  const [entryType, setEntryType] = useState("income"); // 'income' or 'expense'


  const categories = useMemo(
    () => [
      "Platform Income",
      "Cash Income",
      "Payment App Income",
      "Affiliate Income",
      "Cell Phone",
      "Contract Labor",
      "Dues & Subscriptions",
      "Electronics & Equipment",
      "Internet",
      "Legal & Professional",
      "License & Fees",
      "Management Dues",
      "Meals with Clients",
      "Office Supplies",
      "Payroll: Fees",
      "Payroll: Taxes",
      "Payroll: Wages",
      "Photography/Videography",
      "Postage & Shipping",
      "Referral Dues",
      "Rent",
      "Security",
      "Tax Payment",
      "Travel",
      "Uniforms, Costumes, Props",
      "Utilities",
      "Venue Rental",
      "Vehicle Expenses",
      "Website Services/Hosting",
      "Appearance - Temporary Work Only",
      "Advertising & Promo",
      "Tip Outs",
      "House Fees",
      "App Subscriptions",
      "Internet",
      "Phone",
      "Accountant & Attorney",
      "Dancing License",
      "Client/ Networking Meals",
      "Office Supplies",
      "Parking & Tolls",
      "Rent Home",
      "Security",
      "Travel: Airfare",
      "Travel: Hotels",
      "Travel: Meals",
      "Travel: Vehicle Rental",
      "Travel: Uber/Lyft/Taxi",
      "Dancewear",
      "Accessories",
      "Utilities Home",
      "Health Insurance",
    ],
    []
  );

  const sections = useMemo(
    () => [
      {
        title: "Content Income",
        items: [
          "Conent Income",
          "Platform Income",
          "Content 1099 income"
        ],
      },
      {
        title: "Content Expenses",
        items: [
          "Appearance",
          "Promo",
          "Assistant",
          "Assistant 2",
          "Collab 1",
          "Collab 2",
          "Collab 3",
          "Collab 4",
          "Collab 5",
          "Subscriptions & Apps",
          "Electronics",
          "Internet/Phone",
          "Management",
          "Business Operations",
          "Accountant & Attorney",
          "Licenses & Fees",
          "Background Checks",
          "Diagnostic Testing",
          "Client/Networking Meals",
          "Office Supplies",
          "Photography/Videography",
          "Shipping",
          "Merchandise",
          "LLC Biz Fee",
          "Home Rent",
          "Studio Rent",
          "Security - Tech",
          "Security - Physical",
          "Travel: Airfare",
          "Travel: Hotels",
          "Travel: Meals",
          "Travel: Vehicle Rental",
          "Travel: Uber/Lyft/Taxi",
          "Costumes & Uniforms",
          "Toys & Accessories",
          "Studio Utilities",
          "Home Utilities",
          "Website Hosting & Services",
          "Continuing Education",
          "Events & Seminars",
          "Custom Request Expense",
          "Health Insurance",
          "Ask my Accountant",
        ],
      },
      {
        title: "Dancing Income",
        items: ["Cash, 1099 income", "Third party payment app"],
      },
      {
        title: "Dancing Expenses",
        items: [
          "Appearance - Temporary Work Only",
          "Advertising & Promo",
          "Tip Outs",
          "House Fees",
          "App Subscriptions",
          "Internet",
          "Phone",
          "Accountant & Attorney",
          "Dancing License",
          "Client/ Networking Meals",
          "Office Supplies",
          "Parking & Tolls",
          "Rent Home",
          "Security",
          "Travel: Airfare",
          "Travel: Hotels",
          "Travel: Meals",
          "Travel: Vehicle Rental",
          "Travel: Uber/Lyft/Taxi",
          "Dancewear",
          "Accessories",
          "Utilities Home",
          "Health Insurance",
          "Bank Fees",
          "Pole Classes",
          "Continuing Education & Seminars",
          "LLC Fees",
          "Ask my Accountant",
        ],
      },
      {
        title: "Dancing  - Miles Driven Other Income",
        items: [
          "Cash Income",
          "Income Payment App",
          "Agency Income",
          "1099 Income",
        ],
      },
      {
        title: "Other Expenses",
        items: [
          "Collab 2",
          "Collab 3",
          "Contractor Expense",
          "App Subscriptions",
          "Electronics",
          "Internet",
          "Phone",
          "Attorney & Accountant",
          "Licenses & Fees",
          "Background Checks",
          "Diagnostic Testing",
          "House Fees",
          "Other/Networking Meals",
          "Office Supplies",
          "Parking & Tolls",
          "Photography/Videography",
          "Shipping",
          "Merchandise",
          "Rent Home",
          "Rent Studio",
          "Security",
          "Travel: Airfare",
          "Travel: Hotels",
          "Travel: Meals",
          "Travel: Vehicle Rental",
          "Travel: Uber/Lyft/Taxi",
          "Utilities - Home",
          "Utilities - Studio",
          "Continuing Education & Seminars",
          "Ask my Accountant",
          "Mentorship",
          "Business Operations",
          "Health Insurance",
          "LLC Fees",
          "Bank Fees",
          "Custom Request Expense",
        ],
      },
    ],
    []
  );

  // Filter sections based on entryType: income -> only *Income* sections, expense -> only non-Income sections
  const activeSections = useMemo(() => {
    const isIncome = (title) => /income/i.test(title);
    return sections.filter((s) => (entryType === "income" ? isIncome(s.title) : !isIncome(s.title)));
  }, [sections, entryType]);

  // derive the set of all possible items (from active sections)
  const allItems = useMemo(() => {
    const set = new Set();
    activeSections.forEach((s) => s.items.forEach((i) => set.add(i)));
    return Array.from(set);
  }, [activeSections]);

  const tokenizedQuery = useMemo(() => tokenize(query), [query]);

  const searchResults = useMemo(() => {
    const qTokens = tokenizedQuery;
    if (qTokens.length === 0) {
      return allItems.map((item) => ({ item, score: 0 }));
    }
    const results = [];
    for (const candidate of allItems) {
      const { score, matchedCount } = scoreCandidateForTokens(
        candidate,
        qTokens
      );
      if (matchedCount === qTokens.length && score > 0) {
        const candidateNorm = normalizeText(candidate);
        const qFull = normalizeText(query);
        let finalScore = score;
        if (candidateNorm.startsWith(qFull)) finalScore += 40;
        finalScore += Math.max(0, 10 - candidateNorm.split(" ").length);
        results.push({ item: candidate, score: finalScore });
      }
    }
    results.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return a.item.localeCompare(b.item);
    });
    return results;
  }, [allItems, tokenizedQuery, query]);

  const matchedSet = useMemo(
    () => new Set(searchResults.map((r) => r.item)),
    [searchResults]
  );

  const visibleFlattened = useMemo(() => {
    if (!query.trim()) {
      return activeSections.flatMap((s) => s.items);
    }
    const list = [];
    activeSections.forEach((s) => {
      s.items.forEach((it) => {
        if (matchedSet.has(it)) list.push(it);
      });
    });
    searchResults.forEach((r) => {
      if (!list.includes(r.item) && matchedSet.has(r.item)) list.push(r.item);
    });
    return list;
  }, [query, activeSections, matchedSet, searchResults]);

  useEffect(() => {
    itemRefs.current = [];
  }, [visibleFlattened.length]);

  useEffect(() => {
    function onClickOutside(e) {
      const inDropdown =
        dropdownRef.current && dropdownRef.current.contains(e.target);
      const inTrigger =
        triggerRef.current && triggerRef.current.contains(e.target);
      const inPortal =
        portalRef.current && portalRef.current.contains(e.target);
      if (!inDropdown && !inTrigger && !inPortal) {
        setOpen(false);
        setFocusedIndex(-1);
      }
    }
    function onKey(e) {
      if (e.key === "Escape") {
        setOpen(false);
        setFocusedIndex(-1);
      }
    }
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  useEffect(() => {
    if (!open || !triggerRef.current) return;
    function updatePos() {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPos({
        top: Math.round(rect.bottom + 4),
        left: Math.round(rect.left),
        width: Math.round(rect.width),
      });
    }
    updatePos();
    window.addEventListener("resize", updatePos);
    window.addEventListener("scroll", updatePos, true);
    return () => {
      window.removeEventListener("resize", updatePos);
      window.removeEventListener("scroll", updatePos, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (visibleFlattened.length > 0) {
      setFocusedIndex(0);
      setTimeout(() => {
        const el = itemRefs.current[0];
        if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
      }, 0);
    } else {
      setFocusedIndex(-1);
    }
  }, [open, visibleFlattened.length]);

  useEffect(() => {
    if (focusedIndex >= 0 && itemRefs.current[focusedIndex]) {
      const el = itemRefs.current[focusedIndex];
      if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
    }
  }, [focusedIndex]);

  function onSearchKeyDown(e) {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (visibleFlattened.length === 0) return;
      setFocusedIndex((prev) => {
        const next =
          prev < 0 ? 0 : Math.min(prev + 1, visibleFlattened.length - 1);
        return next;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (visibleFlattened.length === 0) return;
      setFocusedIndex((prev) => {
        const next = prev <= 0 ? visibleFlattened.length - 1 : prev - 1;
        return next;
      });
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (focusedIndex >= 0 && visibleFlattened[focusedIndex]) {
        const item = visibleFlattened[focusedIndex];
        setSelectedCategory(item);
        setQuery(item);
        setOpen(false);
        setFocusedIndex(-1);
      }
    }
  }

  function resetForm() {
    setDate("");
    setAmount("");
    setSelectedCategory("");
    setQuery("");
    setOpen(false);
    setFocusedIndex(-1);
    setShowThankYou(false);
    setShowThanksText(false);
    setEntryType("income");
  }


  return (
    <div className="w-full max-w-full sm:max-w-[500px] min-h-screen md:min-h-[550px] flex-col bg-pink-50 flex items-center mx-auto">
      <Header />

      <div className="w-full px-2 sm:px-4 py-3 sm:py-6">
        <div className="bg-[#f3ebe3] rounded-md shadow-xl border border-gray-300 overflow-hidden">
          <div className="w-full flex items-center justify-between px-3 pt-3">
            <div className="w-10 flex justify-center">
              {!showThankYou && (
                <Link
                  to="/tax-help"
                  className="w-10 h-10 text-[#f7adc4] rounded-full font-semibold transition transform hover:-translate-x-0.5 hover:scale-[1.02] active:scale-[0.98] grid place-items-center"
                >
                  <svg
                    className="h-6 w-6"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4A1 1 0 018.707 6.293L6.414 8.586H17a1 1 0 110 2H6.414l2.293 2.293a1 1 0 010 1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </Link>
              )}
            </div>
            <h2 className="flex-1 font-semibold text-center text-sm sm:text-base md:text-[17px] tracking-wide">
              {showThankYou
                ? showThanksText
                  ? ""
                  : `Processing your ${entryType === "income" ? "Income" : "Expense"}...`
                : "INCOME & EXPENSE TRACKER"}
            </h2>
            <div className="w-10" />
          </div>

        {/* Form Body */}
        <div className="p-4 sm:p-6 flex flex-col space-y-3 sm:space-y-4">
          {!showThankYou ? (
            <form
              className={`space-y-4 ${shake ? "animate-shake" : ""} ${success ? "animate-successPulse" : ""
                }`}
              onSubmit={(e) => e.preventDefault()}
            >
              {/* Dual Buttons for Income and Expense */}
              <div className="flex w-100 bg-pink-200 rounded-full p-1">
                <button
                  type="button"
                  onClick={() => setEntryType("income")}
                  className={`flex-1 text-base font-semibold py-2 rounded-full transition-all duration-300
                  ${entryType === "income"
                      ? "bg-pink-300 text-black shadow-sm"
                      : "bg-transparent text-gray-700 hover:bg-[#f3ebe3] hover:text-black"
                    }`}
                >
                  Income
                </button>

                <button
                  type="button"
                  onClick={() => setEntryType("expense")}
                  className={`flex-1 text-base font-semibold py-2 rounded-full transition-all duration-300
                  ${entryType === "expense"
                      ? "bg-pink-400 text-black shadow-sm"
                      : "bg-transparent text-gray-700 hover:bg-[#f3ebe3] hover:text-black"
                    }`}
                >
                  Expense
                </button>
              </div>

              {/* Category Dropdown */}
              <div>
                <label className="text-xs sm:text-sm font-semibold mb-1 block">
                  CATEGORY{" "}
                </label>
                <div className="relative" ref={dropdownRef}>
                  <button
                    type="button"
                    onClick={() => {
                      setOpen((v) => !v);
                      setTimeout(() => {
                        if (inputRef.current) inputRef.current.focus();
                      }, 30);
                    }}
                    className="w-full border border-gray-300 bg-white rounded-md px-3 py-2 text-sm flex items-center justify-between focus:ring-2 focus:ring-[#f7adc4] outline-none"
                    aria-haspopup="listbox"
                    aria-expanded={open}
                    ref={triggerRef}
                  >
                    <span className={selectedCategory ? "" : "text-gray-500"}>
                      {selectedCategory || "Select category"}
                    </span>
                    <svg
                      className={`h-4 w-4 transition-transform duration-200 ${open ? "rotate-180" : ""
                        }`}
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>

                  {open && (
                    <div
                      ref={portalRef}
                      className="absolute left-0 right-0 top-full mt-1 bg-white shadow-lg border border-gray-300 rounded-md z-50 max-h-60 overflow-auto"
                    >
                      <div className="p-2 border-b border-gray-200">
                        <input
                          ref={inputRef}
                          autoFocus
                          type="text"
                          placeholder="Search category..."
                          value={query}
                          onChange={(e) => {
                            setQuery(e.target.value);
                            setFocusedIndex(0);
                          }}
                          onKeyDown={onSearchKeyDown}
                          className="w-full border border-gray-300 bg-white rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#f7adc4] outline-none"
                        />
                      </div>

                      <div className="max-h-48 overflow-auto">
                        {activeSections.map((section, sidx) => {
                          const itemsToShow = !query.trim()
                            ? section.items
                            : section.items.filter((c) => matchedSet.has(c));
                          if (!itemsToShow || itemsToShow.length === 0)
                            return null;
                          return (
                            <div key={sidx}>
                              <div className="px-3 py-1 bg-gray-100 text-[11px] font-bold uppercase tracking-wider text-gray-600 border-b border-gray-200">
                                {section.title}
                              </div>
                              {itemsToShow.map((c) => {
                                const globalIndex = visibleFlattened.indexOf(c);
                                return (
                                  <button
                                    key={c + sidx}
                                    type="button"
                                    ref={(el) => {
                                      if (
                                        typeof globalIndex === "number" &&
                                        globalIndex >= 0
                                      ) {
                                        itemRefs.current[globalIndex] = el;
                                      }
                                    }}
                                    onClick={() => {
                                      setSelectedCategory(c);
                                      setQuery(c);
                                      setOpen(false);
                                      setFocusedIndex(-1);
                                    }}
                                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 transition-colors border-b border-gray-100 ${selectedCategory === c
                                      ? "bg-[#f7adc4] text-white"
                                      : ""
                                      }`}
                                  >
                                    {c}
                                  </button>
                                );
                              })}
                            </div>
                          );
                        })}

                        {query.trim() && searchResults.length === 0 && (
                          <div className="px-3 py-2 text-sm text-gray-500 text-center">
                            No results found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* Amount Input */}
              <div>
                <label className="text-xs sm:text-sm font-semibold mb-1 block">
                  AMOUNT
                </label>
                <input
                  type="number"
                  required
                  inputMode="decimal"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full border border-gray-300 bg-white rounded-md px-3 py-2 text-sm sm:text-base focus:ring-2 focus:ring-[#f7adc4] outline-none"
                />
              </div>
              {/* Date Input */}
              <div>
                <label className="text-xs sm:text-sm font-semibold mb-1 block">DATE</label>
                <input
                  type="date"
                  required
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full border border-gray-300 bg-white rounded-md px-3 py-2 text-sm sm:text-base focus:ring-2 focus:ring-[#f7adc4] outline-none"
                />
              </div>
            </form>
          ) : (
            <div className="text-center py-4">
              {!showThanksText ? (
                <>
                  <img
                    src="/kajabi.gif"
                    alt="Processing"
                    className="h-32 w-32 object-cover mx-auto mb-4 rounded-md"
                  />

                </>
              ) : (
                <>
                  <p className="text-base text-gray-800 mb-2 font-semibold">
                    Your {entryType} has been successfully recorded.
                  </p>

                  <p className="text-sm text-gray-600 mb-4">
                    Click Here to {" "}
                    <Link
                      to="/widget"
                      onClick={resetForm}
                      className="font-semibold text-[#f7adc4] hover:text-[#f49fb8] underline"
                    >
                      Add another entry
                    </Link>

                  </p>
                </>
              )}
            </div>
          )}
          <div
            className="flex gap-3 animate-fadeUp staggered mt-6"
            style={{ "--d": 5 }}
          >
            {!showThankYou ? (
              <button
                type="button"
                onClick={async () => {
                  const isValid = Boolean(date && amount && selectedCategory);
                  if (!isValid) {
                    setShake(true);
                    setTimeout(() => setShake(false), 500);
                    return;
                  }
                  setSuccess(true);
                  setTimeout(() => setSuccess(false), 800);
                  try {
                    // 1) Save to Firestore
                    let saveOk = true;
                    try {
                      await addDoc(collection(db, 'records'), {
                        amount: Number(amount),
                        category: selectedCategory,
                        date: new Date(date),
                        createdAt: serverTimestamp(),
                        type: entryType,
                        userId: auth.currentUser?.uid || 'anonymous',
                      });
                      // console.log('Saved to Firestore');
                    } catch (e) {
                      saveOk = false;
                      console.error('Firestore error:', e?.code, e?.message);
                      setMessage('Could not save to database. Retrying later…');
                      setTimeout(() => setMessage(''), 2500);
                    }

                    // 2) Show processing GIF only
                    setShowThankYou(true);
                    setShowThanksText(false);

                    // 3) Wait ~5s (simulated processing)
                    await new Promise((r) => setTimeout(r, 5000));

                    // 4) No download on OK button - only save to database

                    // 5) Reveal thank-you text
                    setShowThanksText(true);

                    // Optional: inform user if save failed
                    if (!saveOk) {
                      setTimeout(() => {
                        setMessage('Saved locally only. Check Firestore config/rules.');
                        setTimeout(() => setMessage(''), 3000);
                      }, 250);
                    }
                  } catch (err) {
                    console.error(err);
                    alert('Something went wrong. Please try again.');
                    setShowThanksText(true);
                  }
                }}
                className="w-full bg-[#f7adc4] text-white tracking-wider rounded-full text-xs sm:text-sm font-semibold py-2.5 sm:py-3 hover:bg-[#f49fb8] transition-colors active:scale-[0.99]"
              >
                Add Entry
              </button>
            ) : (
              !showThanksText && (
                <Link
                  to="/widget"
                  onClick={resetForm}
                  className="w-full bg-[#f7adc4]  text-white text-xs font-semibold py-3 rounded-full text-center hover:bg-[#f49fb8] transition"
                >
                  Add another entry here
                </Link>
              )
            )}
          </div>
          {/* Bottom Buttons */}
          {!showThankYou && (
            <div className="flex flex-col sm:flex-row justify-between gap-2 mt-3">
              <Link
                to="/expense-summary"
                className="flex-1 bg-white border border-gray-300 text-gray-700 text-xs sm:text-sm font-semibold py-2 rounded-md text-center hover:bg-gray-100 transition"
              >
                View Live Report
              </Link>

              <button
                onClick={async () => {
                  try {
                    console.log('Export to Excel clicked');
                    console.log('XLSX library available:', typeof XLSX);
                    console.log('Firebase db available:', !!db);

                    // Fetch all records from Firebase for the current user
                    console.log('Creating query...');
                    const q = firestoreQuery(
                      collection(db, 'records'),
                      where('userId', '==', auth.currentUser?.uid || 'anonymous')
                    );
                    console.log('Query created:', q);

                    console.log('Fetching documents directly...');
                    const querySnapshot = await getDocs(q);
                    console.log('Records fetched:', querySnapshot.size);

                    // Prepare data for Excel
                    const excelData = [];
                    excelData.push(['Category', 'Sub Category', 'Type', 'Amount', 'Date']); // Headers

                    querySnapshot.forEach((doc) => {
                      const data = doc.data();
                      const date = data.date?.toDate ? data.date.toDate() : new Date(data.date);
                      const formattedDate = date.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit'
                      });

                      let mainCategory = '';
                      let subCategory = data.category || '';

                      for (const section of sections) {
                        if (section.items.includes(data.category)) {
                          mainCategory = section.title;
                          subCategory = data.category;
                          break;
                        }
                      }

                      if (!mainCategory) {
                        mainCategory = data.category || '';
                        subCategory = '';
                      }

                      excelData.push([
                        mainCategory,
                        subCategory,
                        data.type || '',
                        data.amount || 0,
                        formattedDate
                      ]);
                    });

                    // Create workbook and worksheet
                    const ws = XLSX.utils.aoa_to_sheet(excelData);
                    const wb = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(wb, ws, 'Records');

                    // Generate Excel file and download
                    const fileName = `Expense_Records_${new Date().toISOString().split('T')[0]}.xlsx`;
                    XLSX.writeFile(wb, fileName);

                  } catch (error) {
                    console.error('Error exporting to Excel:', error);
                    console.error('Error details:', error.message);
                    console.error('Error stack:', error.stack);
                    alert(`Failed to export data: ${error.message}. Please try again.`);
                  }
                }}
                className="flex-1 bg-white border border-gray-300 text-gray-700 text-xs sm:text-sm font-semibold py-2 rounded-md hover:bg-gray-100 transition"
              >
                Export to Excel
              </button>
            </div>
          )}
        </div>
        </div>
      </div>

      {/* Footer */}
      <div className="w-full bg-[#f7adc4] text-white text-center flex justify-center items-center px-4 py-2 mt-auto">
        <img src="/1.png" alt="Logo" className="h-auto w-16 sm:w-20" />
      </div>
    </div>
  );
}

export default Widget;
